import { Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogConfig as MatDialogConfig } from "@angular/material/legacy-dialog";
import { Subscription } from "rxjs";
import { first } from "rxjs/operators";
import { FileValidator, FileInputComponent } from "ngx-material-file-input";
import * as _ from "lodash";

import { AppGlobals } from "../../app.globals";
import { IUser } from "../../_models";
import { OrientationService, AlertService, MediaWatcherService, UserService, StorageService } from "../../_services";
import { UnSavedChangesDialogComponent } from "../../_components/unSavedChangesDialog/unSavedChangesDialog.component";

@Component({
    templateUrl: "orientationPreArrivalFormDialog.component.html",
    styleUrls: ["orientationPreArrivalFormDialog.component.css"]
})
export class OrientationPreArrivalFormDialogComponent implements OnInit, OnDestroy {
    liabilityFormGroup: UntypedFormGroup;
    fitnessFormGroup: UntypedFormGroup;
    photoFormGroup: UntypedFormGroup;
    under18FormGroup: UntypedFormGroup;
    orientationSemesterStudentId = null;
    orientationSemesterStudentRowversion = null;
    scholasticPeriod = null;
    age = null;
    orientationSemesterCheckListItems = [];
    isEditable = true;
    currentUser: IUser;
    allowEdit: boolean;
    isMobile = false;
    mediaWatcherSubscription: Subscription;
    fileName = null;
    attachmentId = null;
    allowedUploadDocumentTypes = null;
    attachmentFileType = null;
    preArrivalCheckListItem = null;
    orientationSemesterId = null;

    fileToUpload: File = null;

    readonly maxSize = 2097152; // 2 MB

    @ViewChild("fileAttachment", { static: true }) attachmentInput: FileInputComponent;

    constructor(private dialogRef: MatDialogRef<OrientationPreArrivalFormDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data: any,
        private formBuilder: UntypedFormBuilder,
        public dialog: MatDialog,
        private appGlobals: AppGlobals,
        private alertService: AlertService,
        private orientationService: OrientationService,
        private mediaWatcherService: MediaWatcherService,
        private storageService: StorageService,
        private userService: UserService) {
        this.currentUser = data.currentUser;
        this.orientationSemesterStudentId = data.orientationSemesterStudentId;
        this.orientationSemesterId = data.orientationSemesterId;
        this.allowEdit = data.allowEdit;
        this.scholasticPeriod = data.scholasticPeriod;
        this.age = data.age;
    }

    ngOnDestroy() {
        this.mediaWatcherSubscription.unsubscribe();
    }

    ngOnInit() {
        //this.googleAnalyticsService.logPageView("/orientationPreArrivalForm");

        this.mediaWatcherSubscription = this.mediaWatcherService.isTabletLarge.subscribe(isMobile => {
            this.isMobile = isMobile;
        });

        this.initializeForm();

        this.onChanges();

        this.orientationService.getStudentOrientationSemester(this.orientationSemesterStudentId)
            .pipe(first())
            .subscribe(result => {
                this.orientationSemesterStudentRowversion = result.rowversion;

                this.orientationService.getOrientationSemesterStudentSystemCheckListItems(this.orientationSemesterId)
                    .pipe(first())
                    .subscribe(orientationSemesterCheckListItems => {
                        this.orientationSemesterCheckListItems = orientationSemesterCheckListItems;

                        this.preArrivalCheckListItem = this.orientationSemesterCheckListItems.find(f => f.code === "PRARRVLFRM");

                        if (this.preArrivalCheckListItem) {
                            this.fileName = this.preArrivalCheckListItem.attachmentFile.fileName;
                            this.attachmentId = this.preArrivalCheckListItem.attachmentId;
                            this.attachmentFileType = this.preArrivalCheckListItem.attachmentFile.fileExtension;
                            this.allowedUploadDocumentTypes = _.chain(this.preArrivalCheckListItem.orientationCheckListItemAllowedDocumentTypes).map("checkListItemDocumentType.fileExtensions").uniq().join().split(",").uniq().join().value();
                        }
                    });
            });
    }

    initializeForm() {
        this.liabilityFormGroup = this.formBuilder.group({
            liabilityConsent: [{ value: (this.allowEdit ? "" : true), disabled: !this.allowEdit }, Validators.requiredTrue]
        });

        this.fitnessFormGroup = this.formBuilder.group({
            fitnessConsent: [{ value: (this.allowEdit ? "" : true), disabled: !this.allowEdit }, Validators.requiredTrue]
        });

        this.photoFormGroup = this.formBuilder.group({
            photoConsent: [{ value: (this.allowEdit ? "" : true), disabled: !this.allowEdit }, Validators.requiredTrue]
        });

        this.under18FormGroup = this.formBuilder.group({
            fileAttachment: [{ value: "", disabled: !this.allowEdit }, [Validators.required, FileValidator.maxContentSize(this.maxSize)]]
        });
    }

    onChanges() {

    }

    get liabilityFormControls() { return this.liabilityFormGroup.controls as any; }

    get fitnessFormControls() { return this.fitnessFormGroup.controls as any; }

    get photoFormControls() { return this.photoFormGroup.controls as any; }

    get under18FormControls() { return this.under18FormGroup.controls as any; }

    saveDetails(submitBtn: HTMLButtonElement) {
        if (this.age >= 18) {
            if (this.photoFormGroup.invalid) {
                return;
            }
        } else {
            //under 18 - add checklist item
            if (this.under18FormGroup.invalid || !this.customValidation()) {
                if (this.under18FormControls.fileAttachment.invalid) {
                    this.under18FormControls.fileAttachment.markAsTouched({ onlySelf: true });
                }

                return;
            }
        }

        if (this.photoFormGroup.dirty || this.under18FormGroup.dirty) {
            setTimeout(() => {
                submitBtn.disabled = true;
            }, 0);

            const checkListItemDetailsModel = {
                orientationSemesterStudentId: this.orientationSemesterStudentId,
                fileName: this.under18FormControls.fileAttachment.value ? this.under18FormControls.fileAttachment.value.files[0].name : null,
                fileType: this.under18FormControls.fileAttachment.value ? this.under18FormControls.fileAttachment.value.files[0].type : null,
                orientationSemesterCheckListItemId: this.age >= 18 ? null : this.preArrivalCheckListItem.orientationSemesterCheckListItems.find(f => f.orientationSemesterId === this.orientationSemesterId).id,
                rowversion: this.orientationSemesterStudentRowversion
            };

            const saveModel = new FormData();

            saveModel.append("checkListItemDetailModel", JSON.stringify(checkListItemDetailsModel));

            if (this.under18FormControls.fileAttachment.value)
                saveModel.append("fileAttachment", this.under18FormControls.fileAttachment.value.files[0], this.under18FormControls.fileAttachment.value.files[0].name);

            this.orientationService.submitPreArrivalForm(saveModel)
                .pipe(first())
                .subscribe(() => {
                    //remove the pre arrival form resubmission notification
                    if (this.currentUser.userNotifications.find(f => f.code === "requiresPreArrivalFormResubmission")) {
                        _.remove(this.currentUser.userNotifications, f => f.code === "requiresPreArrivalFormResubmission");

                        this.storageService.store(this.appGlobals.localStorageKeys.currentUser, this.currentUser);
                    }

                    this.alertService.showMessage("Pre Arrival Form Successfully Submitted.", "Orientation", this.appGlobals.alertMessageTypes.success);

                    this.dialogRef.close();
                });
        }
    }

    customValidation() {
        if (this.under18FormControls.fileAttachment.value && _.find(this.under18FormControls.fileAttachment.value.files, (f: File) => {
            return f.type !== "application/pdf";
        })) {
            this.alertService.showMessage("Only pdf documents are allowed.", "Orientation Pre Arrival", this.appGlobals.alertMessageTypes.error);

            return false;
        }

        return true;
    }


    close() {
        if (this.liabilityFormGroup.dirty || this.fitnessFormGroup.dirty || this.photoFormGroup.dirty || this.under18FormGroup.dirty) {
            //show unsaved changes dialog
            const dialogConfig = new MatDialogConfig();

            dialogConfig.disableClose = true;
            dialogConfig.autoFocus = true;
            dialogConfig.closeOnNavigation = true;
            dialogConfig.width = this.isMobile ? "60vw" : "30vw";
            dialogConfig.height = "auto";
            dialogConfig.panelClass = "unSavedChanges-dialog-container";

            const dialogRef = this.dialog.open(UnSavedChangesDialogComponent, dialogConfig);

            dialogRef.afterClosed().subscribe(result => {
                if (result === "confirm") {
                    this.dialogRef.close();
                }
            });
        } else {
            this.dialogRef.close();
        }
    }

    handleFileInput(files: FileList) {
        this.fileToUpload = files.item(0);
    }

    openDocument(attachmentId: number) {
        this.userService.openAttachment(attachmentId)
            .pipe(first())
            .subscribe((response: any) => {
                const file = new Blob([response], { type: this.attachmentFileType });
                var fileUrl = URL.createObjectURL(file);
                window.open(fileUrl);
            });
    }
}